import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ProfileSection from './components/ProfileSection';
import SchoolBrandingSection from './components/SchoolBrandingSection';
import NotificationSection from './components/NotificationSection';
import StudentDefaultsSection from './components/StudentDefaultsSection';
import DangerZoneSection from './components/DangerZoneSection';

const TeacherSettings = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [loadingTeacher, setLoadingTeacher] = useState(true);
  const [activeSection, setActiveSection] = useState('profile');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/teacher-login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    const fetchTeacher = async () => {
      setLoadingTeacher(true);
      const { data, error } = await supabase
        ?.from('teachers')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.single();
      if (!error && data) {
        setTeacher(data);
      } else {
        // Fallback to auth user data
        setTeacher({ id: user?.id, email: user?.email, full_name: user?.user_metadata?.full_name || '' });
      }
      setLoadingTeacher(false);
    };
    fetchTeacher();
  }, [user?.id]);

  const handleUpdate = (updates) => {
    setTeacher(prev => ({ ...prev, ...updates }));
  };

  const navItems = [
    { id: 'profile', label: 'Teacher Profile', icon: 'User' },
    { id: 'branding', label: 'School & Branding', icon: 'Building2' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'defaults', label: 'Student Defaults', icon: 'Users' },
    { id: 'danger', label: 'Danger Zone', icon: 'AlertTriangle', danger: true },
  ];

  if (authLoading || loadingTeacher) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] pt-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Teacher Settings — LearningTrails</title>
      </Helmet>
      <Header />

      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <h1 className="text-2xl font-bold text-foreground">Settings</h1>
            </div>
            <p className="text-muted-foreground ml-11">Manage your account, school branding, and preferences</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar Navigation */}
            <aside className="lg:w-56 flex-shrink-0">
              <nav className="bg-card rounded-xl border border-border p-2 lg:sticky lg:top-24">
                {navItems?.map(({ id, label, icon, danger }) => (
                  <button
                    key={id}
                    onClick={() => setActiveSection(id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 last:mb-0 ${
                      activeSection === id
                        ? danger
                          ? 'bg-error/10 text-error' :'bg-primary/10 text-primary'
                        : danger
                          ? 'text-error/70 hover:bg-error/5 hover:text-error' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon name={icon} size={17} />
                    {label}
                  </button>
                ))}
              </nav>

              {/* Mobile: horizontal scroll nav */}
              <div className="lg:hidden flex gap-2 overflow-x-auto pb-1 mt-0 -mx-4 px-4 scrollbar-hide">
                {/* handled by the sidebar above on mobile stacking */}
              </div>
            </aside>

            {/* Content Area */}
            <div className="flex-1 min-w-0">
              {activeSection === 'profile' && teacher && (
                <ProfileSection teacher={teacher} onUpdate={handleUpdate} />
              )}
              {activeSection === 'branding' && teacher && (
                <SchoolBrandingSection teacher={teacher} onUpdate={handleUpdate} />
              )}
              {activeSection === 'notifications' && teacher && (
                <NotificationSection teacher={teacher} onUpdate={handleUpdate} />
              )}
              {activeSection === 'defaults' && teacher && (
                <StudentDefaultsSection teacher={teacher} onUpdate={handleUpdate} />
              )}
              {activeSection === 'danger' && teacher && (
                <DangerZoneSection teacher={teacher} />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TeacherSettings;
